import React from "react";

import { useLocation, useNavigate, useTranslation } from "utils";

import { Flex, Icon, Progress, Tooltip } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { ScoreCircleChart, ScoreColor } from "pages/app/crop-mix-composer/components/compositionStep/ScoreCircleChart";

export const ScoreSidePanel = () => {
  const { t } = useTranslation("cropMixComposer");
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;

  const { activeCropMixId, aggregateGoalScores, aggregateMatchScore, selectedCrops, selectedGoals } = useCropMixComposerContext();

  const cropCount = selectedCrops.length;
  const familyCount = new Set(selectedCrops.map((crop) => crop.crop.cropFamily)).size;

  const subHeaderText =
    cropCount > 0
      ? t(`page.${CMC_ROUTES.COMPOSITION}.panel.selectedSummary`, { families: familyCount, crops: cropCount })
      : t(`page.${CMC_ROUTES.COMPOSITION}.panel.subHeader`);

  return (
    <Flex vertical data-testid="crop-mix-composer-side-panel" className="side-panel" justify="space-between">
      <Flex className="side-panel-header-container" justify="space-between">
        <Flex className="side-panel-header" vertical>
          <Flex align="center" gap=".5rem">
            <p className="header-title paragraph-m-bold margin-0">{t(`page.${CMC_ROUTES.COMPOSITION}.panel.header`)}</p>
            <Tooltip placement="top" title={t(`page.${CMC_ROUTES.COMPOSITION}.panel.goalProgressInfo`)}>
              <Flex>
                <Icon className="pointer" iconName="tabler/info-circle" size="s" />
              </Flex>
            </Tooltip>
          </Flex>
          <p className="header-sub-title paragraph-m margin-0">{subHeaderText}</p>
        </Flex>
        <Flex className="edit-icon-container">
          <Icon
            className="edit-icon"
            iconName="klim/edit"
            onClick={() => {
              navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.GOALS}${location.search}`);
            }}
          />
        </Flex>
      </Flex>
      <Flex className="score-charts-container" gap="2rem" vertical align="center">
        {selectedGoals.map((goal, idx) => (
          <Flex key={idx} vertical justify="center" align="center" gap=".5rem">
            <ScoreCircleChart
              key={idx}
              score={Math.round(aggregateGoalScores?.[idx]?.score) || 0}
              color={Object.values(ScoreColor)[idx]}
              isPanel
              goal={goal}
              dataTestId={`panel-score-circle-chart-${idx + 1}`}
            />
            <span className="goal-progress-label paragraph-s margin-0">{t(`goals.${goal}`)}</span>
          </Flex>
        ))}
      </Flex>
      <Flex vertical className="match-score-container">
        <Flex justify="space-between" align="center">
          <Flex align="center" gap=".5rem">
            <p className="paragraph-m-bold margin-0">{t(`page.${CMC_ROUTES.COMPOSITION}.panel.locationAndCropRotationMatch`)}</p>
            <Tooltip placement="top" title={t(`page.${CMC_ROUTES.COMPOSITION}.panel.matchScoreInfo`)}>
              <Icon className="pointer" iconName="tabler/info-circle" size="s" />
            </Tooltip>
          </Flex>
          <Icon
            className="edit-icon"
            iconName="klim/edit"
            onClick={() => {
              navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.SETTINGS}${location.search}`);
            }}
          />
        </Flex>
        <Flex vertical align="end">
          <p className="paragraph-m-bold margin-0">{`${aggregateMatchScore}%`}</p>
          <Progress percent={aggregateMatchScore} showInfo={false} strokeColor="#6D6D6D" />
        </Flex>
      </Flex>
    </Flex>
  );
};
